import { getConnector } from '../services/PatientFacingClient';
import { Session, getSessions } from '../services/SessionStore';
import { RootDispatch, RootState } from '../store';
import {
  trackSMSLinkOpened,
  trackAuthenticationCodeEntered,
  trackAuthenticationCodeRequested,
} from '../services/Tracker';
import { ShowMessengerPayload, Pages, Organization } from '../types';
import { ClientConnector } from '../services/sdkConnector/ClientConnector';

export type CurrentPageState = string;

interface Error {
  status: number;
}

const initialState: CurrentPageState = Pages.Initial;

const urlParams = new URLSearchParams(window.location.search);
const MAX_ATTEMPTS = 5;
let token = urlParams.get('t');

// For testing only
export const __setToken = (newToken: string) => {
  token = newToken;
};

export const currentPage = {
  effects: (dispatch: RootDispatch) => ({
    showInputAccessCode() {
      dispatch.currentPage.update(Pages.InputAccessCode);
    },
    async fetchAccessToken() {
      // https://tigertext.atlassian.net/browse/APP-101
      const connector = getConnector() as ClientConnector;
      try {
        if (!token) {
          dispatch.currentPage.update(Pages.Invalid);
          return;
        }

        await connector.client.patients.requestAccessToken(token);
      } catch (error) {
        const extendedError = error as Error;

        if (extendedError.status === 429) {
          dispatch.currentPage.update(Pages.TooManyRequests);
          dispatch.patient.setPatient({ errors: MAX_ATTEMPTS });
          return;
        }
        dispatch.currentPage.update(Pages.GeneralError);
      }
    },
    showInvalid() {
      dispatch.currentPage.update(Pages.Invalid);
    },
    showLoading() {
      dispatch.currentPage.update(Pages.Loading);
    },
    async showMessenger(payload: null | ShowMessengerPayload, rootState: RootState) {
      dispatch.currentPage.update(Pages.Loading);
      // https://tigertext.atlassian.net/browse/APP-101
      const connector = getConnector() as ClientConnector;

      try {
        if (!token) {
          dispatch.currentPage.update(Pages.Invalid);
          return;
        }

        const sessions = getSessions();

        let accessCode: ShowMessengerPayload['accessCode'];
        let dob: ShowMessengerPayload['dob'];

        if (payload) {
          accessCode = payload.accessCode;
          dob = payload.dob;
        }

        const data = await connector.client.patients.login({
          accessToken: accessCode,
          dateOfBirth: dob,
          linkToken: token,
          sessions,
        });
        connector.store.dispatch.patient.setPatient({
          ...data,
          errors: 0,
          needsAuth: [],
        });

        const organizations = await connector.client.organizations.findAll({ findRoles: false });
        let organization: Organization | undefined;
        for (const org of organizations) {
          if (org.id === data.organization_id) {
            organization = org;
            break;
          }
        }

        if (!organization) {
          dispatch.currentPage.update(Pages.Invalid);
          return;
        }

        connector.store.dispatch.organization.setOrganization({
          id: organization.id,
          allowAttachment: organization.isPFAttachmentAllowed,
          allowPhoto: organization.isPFPhotoAllowed,
        });

        if (!organization.isPatientNetworkEnabled) {
          this.showInvalid();
          return;
        }

        const conversation = await connector.client.conversations.find(data.conversation_id, {
          ensureEntities: {
            counterParty: 'full',
            escalationExecution: false,
            groupMembers: 'full',
            messageCounterParties: 'full',
            organization: false,
            touchOnRelatedEntityLoad: false,
          },
          includeMutes: false,
          groupId: data && data.group_id,
          organizationId: data && data.organization_id,
        });
        await connector.selectConversation(conversation);
        trackAuthenticationCodeEntered(
          conversation.counterPartyId,
          data.session_tokens[0].account_token
        );
        dispatch.conversation.markConversationAsRead(true);

        dispatch.currentPage.update(Pages.Messenger);
      } catch (error) {
        const extendedError = error as Error;

        if (extendedError.status === 429) {
          dispatch.currentPage.update(Pages.TooManyRequests);
          dispatch.patient.setPatient({ errors: MAX_ATTEMPTS });
          return;
        }

        connector.store.dispatch.patient.setPatient({
          errors: rootState.patient.errors ? rootState.patient.errors + 1 : 1,
        });
        await dispatch.currentPage.showInputAccessCode();
      }
    },
    showMessengerFake() {
      dispatch.currentPage.update(Pages.Messenger);
    },
    async fetchLoginCheck({ linkToken, sessions }: { linkToken: string; sessions: Session[] }) {
      // https://tigertext.atlassian.net/browse/APP-101
      const connector = getConnector() as ClientConnector;
      return connector.client.patients.loginCheck({
        linkToken,
        sessions,
      });
    },
    async init(payload: unknown, rootState: RootState) {
      if (!token) {
        dispatch.currentPage.update(Pages.Invalid);
        return;
      }
      // https://tigertext.atlassian.net/browse/APP-101
      const connector = getConnector() as ClientConnector;

      try {
        const sessions = getSessions();

        const {
          needs_auth: needsAuth,
          org_name: orgName,
          phone_number: phoneNumber,
          org_logo: orgLogo,
        }: {
          needs_auth?: string[];
          org_name?: string;
          phone_number?: string;
          org_logo?: string;
        } = await this.fetchLoginCheck({ linkToken: token, sessions });

        connector.store.dispatch.organization.setOrganization({
          logo: orgLogo,
          name: orgName,
          phone: phoneNumber,
        });

        if (Array.isArray(needsAuth)) {
          if (!(needsAuth.includes('access_token') || needsAuth.includes('dob'))) {
            dispatch.currentPage.update(Pages.Invalid);
          }
          trackSMSLinkOpened();
          dispatch.patient.setPatient({ needsAuth, errors: rootState.patient.errors });
          if (!needsAuth.includes('access_token') && needsAuth.includes('dob')) {
            this.showInputAccessCode();
          } else {
            dispatch.currentPage.update(Pages.RequestAccessCode);
          }
        } else {
          if (Array.isArray(sessions) && sessions.length === 0) {
            trackSMSLinkOpened();
          }
          dispatch.currentPage.showMessenger(null);
        }
      } catch (error) {
        // console.error(error);
        dispatch.currentPage.update(Pages.Invalid);
      }
    },
    requestAccess() {
      trackAuthenticationCodeRequested();
      this.showInputAccessCode();
      this.fetchAccessToken();
    },
  }),
  reducers: {
    update: (state: CurrentPageState, page: string): CurrentPageState => page,
  },
  state: initialState,
};
